import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { mockJournals } from '../mocks/journals';
import { academicBoard } from '../mocks/editorialBoard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const DataContext = createContext(null);

export const DataProvider = ({ children }) => {
  const [journals, setJournals] = useState([]);
  const [boardMembers, setBoardMembers] = useState([]);
  const [journalsLoading, setJournalsLoading] = useState(true);
  const [boardLoading, setBoardLoading] = useState(true);

  // Fetch journals from backend, fallback to mock
  const fetchJournals = useCallback(async () => {
    setJournalsLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/journals`);
      if (res.ok) {
        const data = await res.json();
        // Merge: backend journals first, then mock journals that aren't in backend
        if (data.length > 0) {
          setJournals(data);
        } else {
          setJournals(mockJournals);
        }
      } else {
        setJournals(mockJournals);
      }
    } catch {
      setJournals(mockJournals);
    }
    setJournalsLoading(false);
  }, []);

  // Fetch editorial board from backend, fallback to mock
  const fetchBoardMembers = useCallback(async () => {
    setBoardLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/editorial-board`);
      if (res.ok) {
        const data = await res.json();
        if (data.length > 0) {
          setBoardMembers(data);
        } else {
          setBoardMembers(academicBoard);
        }
      } else {
        setBoardMembers(academicBoard);
      }
    } catch {
      setBoardMembers(academicBoard);
    }
    setBoardLoading(false);
  }, []);

  // Initial fetch
  useEffect(() => {
    fetchJournals();
    fetchBoardMembers();
  }, [fetchJournals, fetchBoardMembers]);

  // Edit/delete still work for local state (for non-super-admin inline edits)
  const editJournal = (id, updates) =>
    setJournals(prev => prev.map(j => j.id === id ? { ...j, ...updates } : j));

  const deleteJournal = (id) =>
    setJournals(prev => prev.filter(j => j.id !== id));

  const editBoardMember = (id, updates) =>
    setBoardMembers(prev => prev.map(m => m.id === id ? { ...m, ...updates } : m));

  const deleteBoardMember = (id) =>
    setBoardMembers(prev => prev.filter(m => m.id !== id));

  return (
    <DataContext.Provider value={{
      journals, boardMembers,
      journalsLoading, boardLoading,
      editJournal, deleteJournal,
      editBoardMember, deleteBoardMember,
      refreshJournals: fetchJournals,
      refreshBoardMembers: fetchBoardMembers,
    }}>
      {children}
    </DataContext.Provider>
  );
};

export const useData = () => useContext(DataContext);
